// The only React path by which a money amount reaches the DOM (§6 of the
// frontend proposal; §2/§6 of the 2026-09-06 UX execution plan). Sign color
// is never the only signal: a sign prefix and an aria-label always accompany
// the color class. Masking is applied identically to the visible text AND
// the aria-label -- fixing a live defect where the label was previously built
// with `masked: false` unconditionally, leaking the real amount to screen
// readers and to any automation while "Hide balances" was on.
import { formatMoney, useMoneyFormat, type FormatMoneyOptions } from '@/lib/money';
import { GapBar } from './GapBar';

export type AmountVariant = 'amount' | 'balance' | 'delta' | 'transfer';
export type BalanceStatus = 'ok' | 'derived' | 'stale' | 'insufficient';

export function AmountText({
  value,
  currency,
  className,
  variant = 'amount',
  balanceStatus,
  asOf,
  struck = false,
}: {
  value: string | null;
  currency?: string;
  className?: string;
  variant?: AmountVariant;
  balanceStatus?: BalanceStatus;
  asOf?: string | null;
  struck?: boolean;
}) {
  const { currency: defaultCurrency, masked } = useMoneyFormat();
  const resolvedCurrency = currency ?? defaultCurrency;

  // `insufficient` (and, once snapshots ship, an absent value generally) is
  // the ONLY representation of "never reported" -- rendered as a hatched
  // slug, never a dash and never a zero. This is the null-propagation
  // invariant from §6.3 of the execution plan.
  if (value === null || balanceStatus === 'insufficient') {
    return <GapBar label="no balance yet" className={className} />;
  }

  // Line items (income/spend) and deltas always carry an explicit sign
  // ("+$4,210.00" income, "−$1,286.42" spend); an asset/debt balance does
  // not get a "+" for a positive value (design's money table, §1.3).
  const signDisplay: FormatMoneyOptions['signDisplay'] =
    variant === 'transfer' ? 'never' : variant === 'balance' ? 'auto' : 'always';
  const numeric = Number(value);
  // Money colour semantics (founder decision, §14 of the 2026-09-08
  // redesign revision; docs/canonical/product/money-color-semantics.md):
  // expense is red, income is green, transfer is neutral. Sign decides the
  // colour; a transfer-tagged amount is never red/green regardless of sign.
  const signClass =
    variant === 'transfer'
      ? 'text-muted-foreground'
      : numeric > 0
        ? 'text-income'
        : numeric < 0
          ? 'text-expense'
          : '';

  const formatted = formatMoney(value, { currency: resolvedCurrency, masked, signDisplay });
  // The aria-label MUST be computed with the SAME masked flag as the visible
  // text -- computing it with masked:false always is exactly the leak this
  // rewrite fixes.
  const ariaLabel = formatMoney(value, { currency: resolvedCurrency, masked, signDisplay });

  const degraded = balanceStatus === 'derived' || balanceStatus === 'stale';
  const degradedClass = degraded
    ? 'ms-tabular ms-degraded-underline pb-0.5'
    : 'ms-tabular';

  const classes = [
    signClass,
    degradedClass,
    struck ? 'line-through text-muted-foreground' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const prefix = variant === 'transfer' ? '⇄ ' : '';

  // §14 decision 4: the amount column carries no pending text/dot anymore --
  // there is exactly ONE pending indicator per row, the `TypeBadge
  // kind="pending"` under the merchant name (FeedRow/TransactionDetailPanel).
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={classes} aria-label={`${prefix}${ariaLabel}`}>
        {prefix}
        {formatted}
      </span>
      {degraded && asOf && (
        <span className="rounded-full bg-degraded-surface px-1.5 py-0.5 text-[10px] text-degraded-foreground">
          as of {asOf}
        </span>
      )}
    </span>
  );
}
